import {
  Box,
  Button,
  ButtonGroup,
  Container,
  Heading,
  HStack,
  Icon,
  Input,
  Text,
  VStack
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import type { ColDef, GridReadyEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FiCalendar, FiFilter } from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { mockApi } from "../api/mockApi";

// Import AG Grid Enterprise and register modules
import { AgChartsEnterpriseModule } from "ag-charts-enterprise";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import {
  AllEnterpriseModule,
  IntegratedChartsModule
} from "ag-grid-enterprise";

// Register AG Grid modules including charts
ModuleRegistry.registerModules([
  AllCommunityModule,
  AllEnterpriseModule,
  IntegratedChartsModule.with(AgChartsEnterpriseModule)
]);

function Campaigns() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Set default dates to today
  const today = new Date().toISOString().split("T")[0];

  // Initialize state from URL parameters or defaults
  const [startDate, setStartDate] = useState(
    searchParams.get("startDate") || today
  );
  const [endDate, setEndDate] = useState(searchParams.get("endDate") || today);
  const [selectedRange, setSelectedRange] = useState<string>("today");

  // Helper function to set date ranges
  const setDateRange = (range: string) => {
    const today = new Date();
    let endDateObj = new Date(today);
    let startDateObj = new Date(today);

    switch (range) {
      case "today":
        // Both dates are today
        break;
      case "yesterday":
        startDateObj.setDate(today.getDate() - 1);
        endDateObj.setDate(today.getDate() - 1);
        break;
      case "last7":
        startDateObj.setDate(today.getDate() - 6);
        break;
      case "last30":
        startDateObj.setDate(today.getDate() - 29);
        break;
      case "last90":
        startDateObj.setDate(today.getDate() - 89);
        break;
      case "thisMonth":
        startDateObj = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case "lastMonth":
        startDateObj = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDateObj = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        return;
    }

    setStartDate(startDateObj.toISOString().split("T")[0]);
    setEndDate(endDateObj.toISOString().split("T")[0]);
    setSelectedRange(range);
  };

  // Update URL when filters change
  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    newSearchParams.set("startDate", startDate);
    newSearchParams.set("endDate", endDate);
    setSearchParams(newSearchParams, { replace: true });
  }, [startDate, endDate, setSearchParams]);

  const {
    data: campaigns = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ["campaigns", startDate, endDate],
    queryFn: () => mockApi.getCampaigns({ startDate, endDate })
  });

  // AG Grid column definitions
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Account ID",
        field: "account_id",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 120,
        maxWidth: 150,
        cellRenderer: (params: any) => {
          return (
            params.value || (
              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                No ID
              </Text>
            )
          );
        }
      },
      {
        headerName: "Campaign Name",
        field: "campaign_name",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 200,
        maxWidth: 300,
        cellRenderer: (params: any) => {
          return (
            params.value || (
              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                No Campaign Name
              </Text>
            )
          );
        }
      },
      {
        headerName: "Campaign ID",
        field: "campaign_id",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 140,
        maxWidth: 180,
        cellRenderer: (params: any) => {
          return (
            params.value || (
              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                No Campaign ID
              </Text>
            )
          );
        }
      },
      {
        headerName: "Adset Name",
        field: "adset_name",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 160,
        maxWidth: 250,
        cellRenderer: (params: any) => {
          return (
            params.value || (
              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                No Adset Name
              </Text>
            )
          );
        }
      },
      {
        headerName: "Adset ID",
        field: "adset_id",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 140,
        maxWidth: 180,
        cellRenderer: (params: any) => {
          return (
            params.value || (
              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                No Adset ID
              </Text>
            )
          );
        }
      },
      {
        headerName: "Ad Name",
        field: "ad_name",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 150,
        maxWidth: 250,
        cellRenderer: (params: any) => {
          return (
            params.value || (
              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                No Ad Name
              </Text>
            )
          );
        }
      },
      {
        headerName: "Ad ID",
        field: "ad_id",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 120,
        maxWidth: 160,
        cellRenderer: (params: any) => {
          return (
            params.value || (
              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                No Ad ID
              </Text>
            )
          );
        }
      },
      {
        headerName: "Start Date",
        field: "date_start",
        filter: "agDateColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 120,
        maxWidth: 160,
        valueFormatter: (params) => {
          if (!params.value) return "N/A";
          return new Date(params.value).toLocaleDateString();
        },
        cellRenderer: (params: any) => {
          if (!params.value) {
            return (
              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                N/A
              </Text>
            );
          }
          return (
            <Text fontSize="sm">
              {new Date(params.value).toLocaleDateString()}
            </Text>
          );
        }
      },
      {
        headerName: "End Date",
        field: "date_stop",
        filter: "agDateColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 120,
        maxWidth: 160,
        valueFormatter: (params) => {
          if (!params.value) return "N/A";
          return new Date(params.value).toLocaleDateString();
        },
        cellRenderer: (params: any) => {
          if (!params.value) {
            return (
              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                N/A
              </Text>
            );
          }
          return (
            <Text fontSize="sm">
              {new Date(params.value).toLocaleDateString()}
            </Text>
          );
        }
      },
      {
        headerName: "Created",
        field: "created_time",
        filter: "agDateColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 140,
        maxWidth: 180,
        valueFormatter: (params) => {
          if (!params.value) return "N/A";
          return new Date(params.value).toLocaleDateString();
        },
        cellRenderer: (params: any) => {
          if (!params.value) {
            return (
              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                N/A
              </Text>
            );
          }
          return (
            <Text fontSize="sm">
              {new Date(params.value).toLocaleDateString()}
            </Text>
          );
        }
      },
      {
        headerName: "CPC",
        field: "cpc",
        filter: "agNumberColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 100,
        maxWidth: 120,
        valueFormatter: (params) => {
          if (!params.value) return "N/A";
          return `$${parseFloat(params.value).toFixed(2)}`;
        },
        cellRenderer: (params: any) => {
          if (!params.value) {
            return (
              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                N/A
              </Text>
            );
          }
          return (
            <Text fontSize="sm" fontWeight="medium" color="green.600">
              ${parseFloat(params.value).toFixed(2)}
            </Text>
          );
        }
      },
      {
        headerName: "CPM",
        field: "cpm",
        filter: "agNumberColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 100,
        maxWidth: 120,
        valueFormatter: (params) => {
          if (!params.value) return "N/A";
          return `$${parseFloat(params.value).toFixed(2)}`;
        },
        cellRenderer: (params: any) => {
          if (!params.value) {
            return (
              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                N/A
              </Text>
            );
          }
          return (
            <Text fontSize="sm" fontWeight="medium" color="blue.600">
              ${parseFloat(params.value).toFixed(2)}
            </Text>
          );
        }
      },
      {
        headerName: "CTR",
        field: "ctr",
        filter: "agNumberColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 100,
        maxWidth: 120,
        valueFormatter: (params) => {
          if (!params.value) return "N/A";
          return `${parseFloat(params.value).toFixed(2)}%`;
        },
        cellRenderer: (params: any) => {
          if (!params.value) {
            return (
              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                N/A
              </Text>
            );
          }
          const ctr = parseFloat(params.value);
          const color =
            ctr > 2 ? "green.600" : ctr > 1 ? "orange.500" : "red.500";
          return (
            <Text fontSize="sm" fontWeight="medium" color={color}>
              {ctr.toFixed(2)}%
            </Text>
          );
        }
      },
      {
        headerName: "Frequency",
        field: "frequency",
        filter: "agNumberColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 100,
        maxWidth: 130,
        valueFormatter: (params) => {
          if (!params.value) return "N/A";
          return parseFloat(params.value).toFixed(2);
        },
        cellRenderer: (params: any) => {
          if (!params.value) {
            return (
              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                N/A
              </Text>
            );
          }
          return (
            <Text fontSize="sm">{parseFloat(params.value).toFixed(2)}</Text>
          );
        }
      },
      {
        headerName: "Impressions",
        field: "impressions",
        filter: "agNumberColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 120,
        maxWidth: 150,
        valueFormatter: (params) => {
          if (!params.value) return "N/A";
          return parseInt(params.value).toLocaleString();
        },
        cellRenderer: (params: any) => {
          if (!params.value) {
            return (
              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                N/A
              </Text>
            );
          }
          return (
            <Text fontSize="sm" fontWeight="medium" color="purple.600">
              {parseInt(params.value).toLocaleString()}
            </Text>
          );
        }
      },
      {
        headerName: "Spend",
        field: "spend",
        filter: "agNumberColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 100,
        maxWidth: 130,
        valueFormatter: (params) => {
          if (!params.value) return "N/A";
          return `$${parseFloat(params.value).toFixed(2)}`;
        },
        cellRenderer: (params: any) => {
          if (!params.value) {
            return (
              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                N/A
              </Text>
            );
          }
          return (
            <Text fontSize="sm" fontWeight="bold" color="red.600">
              ${parseFloat(params.value).toFixed(2)}
            </Text>
          );
        }
      },
      {
        headerName: "Calendly",
        field: "calendly_events",
        filter: "agNumberColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 100,
        maxWidth: 130,
        valueFormatter: (params) => {
          if (!params.value) return "N/A";
          return parseInt(params.value).toLocaleString();
        },
        cellRenderer: (params: any) => {
          if (!params.value) {
            return (
              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                N/A
              </Text>
            );
          }
          return (
            <Text fontSize="sm" fontWeight="medium" color="teal.600">
              {parseInt(params.value).toLocaleString()}
            </Text>
          );
        }
      },
      {
        headerName: "Conversions",
        field: "conversions",
        filter: "agNumberColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 120,
        maxWidth: 150,
        valueFormatter: (params) => {
          if (!params.value) return "N/A";
          return parseInt(params.value).toLocaleString();
        },
        cellRenderer: (params: any) => {
          if (!params.value) {
            return (
              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                N/A
              </Text>
            );
          }
          return (
            <Text fontSize="sm" fontWeight="bold" color="green.600">
              {parseInt(params.value).toLocaleString()}
            </Text>
          );
        }
      }
    ],
    []
  );

  // Default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 100,
      suppressSizeToFit: true,
      autoHeaderHeight: true
    }),
    []
  );

  // Grid ready event
  const onGridReady = useCallback((event: GridReadyEvent) => {
    console.log("AG Grid is ready!", event.api);
    // Auto-size all columns based on content
    event.api.autoSizeAllColumns();
  }, []);

  // Handle row double-click to navigate to ad detail
  const onRowDoubleClicked = useCallback(
    (event: any) => {
      const rowData = event.data;
      console.log("Row double-clicked:", rowData);

      // Ensure we have data
      if (!rowData) {
        console.warn("No row data available");
        return;
      }

      // Check if we have the required IDs
      const hasAllIds =
        rowData.campaign_id && rowData.adset_id && rowData.ad_id;

      if (hasAllIds) {
        // Preserve current search parameters when navigating
        const currentParams = searchParams.toString();
        const url = `/campaigns/${rowData.campaign_id}/adsets/${rowData.adset_id}/ads/${rowData.ad_id}${currentParams ? `?${currentParams}` : ""}`;
        console.log("Navigating to:", url);
        navigate(url);
      } else {
        // Log which IDs are missing
        const missingIds = [];
        if (!rowData.campaign_id) missingIds.push("campaign_id");
        if (!rowData.adset_id) missingIds.push("adset_id");
        if (!rowData.ad_id) missingIds.push("ad_id");

        console.warn(
          "Cannot navigate - missing required IDs:",
          missingIds.join(", ")
        );
        alert(`Cannot open ad details. Missing: ${missingIds.join(", ")}`);
      }
    },
    [navigate, searchParams]
  );

  if (isLoading) {
    return (
      <Container maxW="7xl" py={8}>
        {/* Header Skeleton */}
        <Box mb={8}>
          <Box h="12" w="200px" bg="gray.200" rounded="md" className="animate-pulse" />
        </Box>

        {/* Date Range Filter Skeleton */}
        <Box
          bg="white"
          py={8}
          px={12}
          rounded="2xl"
          shadow="xl"
          border="1px"
          borderColor="gray.100"
          maxW="900px"
          mx="auto"
          mb={8}
        >
          <VStack gap={6}>
            <Box h="8" w="180px" bg="gray.200" rounded="md" className="animate-pulse" />
            <HStack gap={3}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Box key={i} h="10" w="100px" bg="gray.200" rounded="md" className="animate-pulse" />
              ))}
            </HStack>
            <HStack gap={4} w="full" justify="center">
              <Box h="10" w="150px" bg="gray.200" rounded="md" className="animate-pulse" />
              <Box h="10" w="150px" bg="gray.200" rounded="md" className="animate-pulse" />
              <Box h="10" w="120px" bg="blue.200" rounded="md" className="animate-pulse" />
            </HStack>
          </VStack>
        </Box>

        {/* Stats Cards Skeleton */}
        <HStack gap={8} mb={8} justify="center">
          {[1, 2, 3, 4].map((i) => (
            <Box
              key={i}
              p={8}
              bg="white"
              rounded="2xl"
              shadow="lg"
              border="1px"
              borderColor="gray.100"
              flex={1}
              maxW="250px"
            >
              <VStack align="start" gap={3}>
                <Box h="4" w="100px" bg="gray.200" rounded="md" className="animate-pulse" />
                <Box h="10" w="120px" bg="gray.300" rounded="md" className="animate-pulse" />
                <Box h="3" w="80px" bg="gray.100" rounded="md" className="animate-pulse" />
              </VStack>
            </Box>
          ))}
        </HStack>

        {/* Table Actions Skeleton */}
        <HStack justify="space-between" mb={6}>
          <Box h="10" w="300px" bg="gray.200" rounded="md" className="animate-pulse" />
          <HStack gap={4}>
            <Box h="10" w="140px" bg="gray.200" rounded="md" className="animate-pulse" />
            <Box h="10" w="140px" bg="blue.200" rounded="md" className="animate-pulse" />
          </HStack>
        </HStack>

        {/* Table Skeleton */}
        <Box
          bg="white"
          shadow="2xl"
          rounded="2xl"
          overflow="hidden"
          border="1px"
          borderColor="gray.100"
        >
          <Box p={4} borderBottom="2px" borderColor="gray.200" bg="gray.50">
            <HStack gap={4}>
              {["Campaign", "Status", "Impressions", "Spend", "CPC", "CTR", "Conversions"].map((_, i) => (
                <Box key={i} flex={i === 0 ? 2 : 1}>
                  <Box h="4" w="70%" bg="gray.300" rounded="sm" className="animate-pulse" />
                </Box>
              ))}
            </HStack>
          </Box>
          <VStack align="stretch" p={0} gap={0}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((row) => (
              <Box key={row} p={4} borderBottom="1px" borderColor="gray.100">
                <HStack gap={4}>
                  {[1, 2, 3, 4, 5, 6, 7].map((col) => (
                    <Box key={col} flex={col === 1 ? 2 : 1}>
                      <Box 
                        h="5" 
                        w={`${Math.random() * 30 + 40}%`} 
                        bg="gray.200" 
                        rounded="sm" 
                        className="animate-pulse" 
                      />
                    </Box>
                  ))}
                </HStack>
              </Box>
            ))}
          </VStack>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="7xl" py={8}>
        <Box textAlign="center">
          <Box
            p={4}
            bg="red.50"
            border="1px"
            borderColor="red.200"
            rounded="md"
            mb={4}
          >
            <Text color="red.600" fontWeight="medium">
              Error loading campaigns
            </Text>
            <Text color="red.500" fontSize="sm">
              {error.message}
            </Text>
          </Box>
          <Button
            onClick={() => refetch()}
            bg="#0959ED"
            color="white"
            _hover={{ bg: "#0849D1" }}
            _active={{ bg: "#0739B8" }}
          >
            Retry
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxW="7xl" py={8}>
      {/* Header */}
      <Box mb={8}>
        <Heading size="2xl" color="#191d16">
          Campaigns
        </Heading>
      </Box>

      {/* Date Range Picker - Improved UI */}
      <Box
        bg="white"
        py={8}
        px={12}
        rounded="2xl"
        shadow="xl"
        border="1px"
        borderColor="gray.100"
        maxW="900px"
        mx="auto"
        mb={8}
        transition="all 0.3s ease"
        _hover={{
          shadow: "2xl",
          transform: "translateY(-2px)"
        }}
      >
        <VStack gap={6}>
          {/* Header */}
          <HStack gap={3}>
            <Icon as={FiFilter} w={6} h={6} color="#0959ED" />
            <Heading size="md" color="#191d16">
              Filter Campaigns
            </Heading>
          </HStack>

          {/* Quick Date Range Buttons */}
          <VStack gap={4} w="full">
            <Text fontSize="sm" fontWeight="600" color="gray.600">
              Quick Select
            </Text>
            <ButtonGroup size="sm" variant="outline" gap={3}>
              <Button
                onClick={() => setDateRange("today")}
                bg={selectedRange === "today" ? "#0959ED" : "white"}
                color={selectedRange === "today" ? "white" : "gray.700"}
                borderColor={selectedRange === "today" ? "#0959ED" : "gray.300"}
                transition="all 0.2s ease"
                transform={
                  selectedRange === "today" ? "scale(1.05)" : "scale(1)"
                }
                _hover={{
                  bg: selectedRange === "today" ? "#0849D1" : "gray.50",
                  borderColor: "#0959ED",
                  transform: "scale(1.05)"
                }}
                _active={{
                  transform: "scale(0.95)"
                }}
              >
                Today
              </Button>
              <Button
                onClick={() => setDateRange("yesterday")}
                bg={selectedRange === "yesterday" ? "#0959ED" : "white"}
                color={selectedRange === "yesterday" ? "white" : "gray.700"}
                borderColor={
                  selectedRange === "yesterday" ? "#0959ED" : "gray.300"
                }
                transition="all 0.2s ease"
                transform={
                  selectedRange === "yesterday" ? "scale(1.05)" : "scale(1)"
                }
                _hover={{
                  bg: selectedRange === "yesterday" ? "#0849D1" : "gray.50",
                  borderColor: "#0959ED",
                  transform: "scale(1.05)"
                }}
                _active={{
                  transform: "scale(0.95)"
                }}
              >
                Yesterday
              </Button>
              <Button
                onClick={() => setDateRange("last7")}
                bg={selectedRange === "last7" ? "#0959ED" : "white"}
                color={selectedRange === "last7" ? "white" : "gray.700"}
                borderColor={selectedRange === "last7" ? "#0959ED" : "gray.300"}
                transition="all 0.2s ease"
                transform={
                  selectedRange === "last7" ? "scale(1.05)" : "scale(1)"
                }
                _hover={{
                  bg: selectedRange === "last7" ? "#0849D1" : "gray.50",
                  borderColor: "#0959ED",
                  transform: "scale(1.05)"
                }}
                _active={{
                  transform: "scale(0.95)"
                }}
              >
                Last 7 Days
              </Button>
              <Button
                onClick={() => setDateRange("last30")}
                bg={selectedRange === "last30" ? "#0959ED" : "white"}
                color={selectedRange === "last30" ? "white" : "gray.700"}
                borderColor={
                  selectedRange === "last30" ? "#0959ED" : "gray.300"
                }
                transition="all 0.2s ease"
                transform={
                  selectedRange === "last30" ? "scale(1.05)" : "scale(1)"
                }
                _hover={{
                  bg: selectedRange === "last30" ? "#0849D1" : "gray.50",
                  borderColor: "#0959ED",
                  transform: "scale(1.05)"
                }}
                _active={{
                  transform: "scale(0.95)"
                }}
              >
                Last 30 Days
              </Button>
              <Button
                onClick={() => setDateRange("last90")}
                bg={selectedRange === "last90" ? "#0959ED" : "white"}
                color={selectedRange === "last90" ? "white" : "gray.700"}
                borderColor={
                  selectedRange === "last90" ? "#0959ED" : "gray.300"
                }
                transition="all 0.2s ease"
                transform={
                  selectedRange === "last90" ? "scale(1.05)" : "scale(1)"
                }
                _hover={{
                  bg: selectedRange === "last90" ? "#0849D1" : "gray.50",
                  borderColor: "#0959ED",
                  transform: "scale(1.05)"
                }}
                _active={{
                  transform: "scale(0.95)"
                }}
              >
                Last 90 Days
              </Button>
              <Button
                onClick={() => setDateRange("thisMonth")}
                bg={selectedRange === "thisMonth" ? "#0959ED" : "white"}
                color={selectedRange === "thisMonth" ? "white" : "gray.700"}
                borderColor={
                  selectedRange === "thisMonth" ? "#0959ED" : "gray.300"
                }
                transition="all 0.2s ease"
                transform={
                  selectedRange === "thisMonth" ? "scale(1.05)" : "scale(1)"
                }
                _hover={{
                  bg: selectedRange === "thisMonth" ? "#0849D1" : "gray.50",
                  borderColor: "#0959ED",
                  transform: "scale(1.05)"
                }}
                _active={{
                  transform: "scale(0.95)"
                }}
              >
                This Month
              </Button>
              <Button
                onClick={() => setDateRange("lastMonth")}
                bg={selectedRange === "lastMonth" ? "#0959ED" : "white"}
                color={selectedRange === "lastMonth" ? "white" : "gray.700"}
                borderColor={
                  selectedRange === "lastMonth" ? "#0959ED" : "gray.300"
                }
                transition="all 0.2s ease"
                transform={
                  selectedRange === "lastMonth" ? "scale(1.05)" : "scale(1)"
                }
                _hover={{
                  bg: selectedRange === "lastMonth" ? "#0849D1" : "gray.50",
                  borderColor: "#0959ED",
                  transform: "scale(1.05)"
                }}
                _active={{
                  transform: "scale(0.95)"
                }}
              >
                Last Month
              </Button>
            </ButtonGroup>
          </VStack>

          <Box w="full" h="1px" bg="gray.200" />

          {/* Custom Date Range */}
          <VStack gap={4} w="full">
            <Text fontSize="sm" fontWeight="600" color="gray.600">
              Custom Range
            </Text>
            <HStack gap={4} justify="center">
              <VStack align="start" gap={2}>
                <HStack gap={2}>
                  <Icon as={FiCalendar} w={4} h={4} color="gray.500" />
                  <Text fontSize="sm" fontWeight="500" color="gray.700">
                    From
                  </Text>
                </HStack>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setSelectedRange("custom");
                  }}
                  size="md"
                  width="180px"
                  borderColor="gray.300"
                  borderRadius="lg"
                  bg="gray.50"
                  _hover={{ borderColor: "#0959ED", bg: "white" }}
                  _focus={{
                    borderColor: "#0959ED",
                    boxShadow: "0 0 0 1px #0959ED",
                    bg: "white"
                  }}
                />
              </VStack>

              <Box pt={6}>
                <Text color="gray.400" fontSize="lg">
                  →
                </Text>
              </Box>

              <VStack align="start" gap={2}>
                <HStack gap={2}>
                  <Icon as={FiCalendar} w={4} h={4} color="gray.500" />
                  <Text fontSize="sm" fontWeight="500" color="gray.700">
                    To
                  </Text>
                </HStack>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setSelectedRange("custom");
                  }}
                  size="md"
                  width="180px"
                  borderColor="gray.300"
                  borderRadius="lg"
                  bg="gray.50"
                  _hover={{ borderColor: "#0959ED", bg: "white" }}
                  _focus={{
                    borderColor: "#0959ED",
                    boxShadow: "0 0 0 1px #0959ED",
                    bg: "white"
                  }}
                />
              </VStack>
            </HStack>

            {/* Filter Button */}
            <Button
              bg="#0959ED"
              color="white"
              size="lg"
              px={12}
              py={6}
              onClick={() => refetch()}
              borderRadius="xl"
              fontWeight="600"
              letterSpacing="wide"
              _hover={{
                bg: "#0849D1",
                transform: "translateY(-2px)",
                shadow: "2xl"
              }}
              _active={{
                bg: "#0739B8",
                transform: "translateY(0px)"
              }}
              disabled={
                !startDate ||
                !endDate ||
                new Date(startDate) > new Date(endDate)
              }
              _disabled={{
                bg: "gray.300",
                color: "gray.500",
                cursor: "not-allowed",
                _hover: { bg: "gray.300", transform: "none", shadow: "none" }
              }}
              w="full"
              maxW="300px"
            >
              <HStack gap={2}>
                <Icon as={FiFilter} w={5} h={5} />
                <Text>Filter Campaigns</Text>
              </HStack>
            </Button>

            {/* Error message for invalid date range */}
            {startDate &&
              endDate &&
              new Date(startDate) > new Date(endDate) && (
                <Text fontSize="sm" color="red.500">
                  Start date must be before or equal to end date
                </Text>
              )}
          </VStack>
        </VStack>
      </Box>

      {/* Instructions */}
      <Box
        bg="blue.50"
        border="1px solid"
        borderColor="blue.200"
        rounded="lg"
        p={3}
        mb={6}
        textAlign="center"
      >
        <Text fontSize="sm" color="blue.700">
          💡 Double-click on any row to view detailed ad information
        </Text>
      </Box>

      {campaigns.length === 0 ? (
        <Box p={12} textAlign="center" bg="white" rounded="lg" shadow="md">
          <Heading size="lg" color="#191d16" mb={4}>
            No campaigns found
          </Heading>
          <Text color="gray.600">
            No campaigns match the selected date range.
          </Text>
        </Box>
      ) : (
        <Box
          bg="white"
          rounded="lg"
          shadow="md"
          overflow="hidden"
          border="1px solid"
          borderColor="gray.200"
        >
          <Box height="700px" width="100%">
            <AgGridReact
              rowData={campaigns}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              onRowDoubleClicked={onRowDoubleClicked}
              getRowStyle={(params) => {
                const hasAllIds =
                  params.data?.campaign_id &&
                  params.data?.adset_id &&
                  params.data?.ad_id;
                return {
                  cursor: hasAllIds ? "pointer" : "not-allowed",
                  opacity: hasAllIds ? 1 : 0.7
                };
              }}
              onFirstDataRendered={(params) => {
                // Auto-size columns when data is first rendered
                params.api.autoSizeAllColumns();
              }}
              onGridSizeChanged={(params) => {
                // Auto-size columns when grid is resized
                params.api.autoSizeAllColumns();
              }}
              animateRows={true}
              cellSelection={true}
              enableCharts={true}
              sideBar={{
                toolPanels: [
                  {
                    id: "columns",
                    labelDefault: "Columns",
                    labelKey: "columns",
                    iconKey: "columns",
                    toolPanel: "agColumnsToolPanel"
                  },
                  {
                    id: "filters",
                    labelDefault: "Filters",
                    labelKey: "filters",
                    iconKey: "filter",
                    toolPanel: "agFiltersToolPanel"
                  }
                ],
                hiddenByDefault: true
              }}
              rowSelection={{
                mode: "singleRow",
                enableClickSelection: false
              }}
              pagination={true}
              paginationPageSize={10}
              paginationPageSizeSelector={[5, 10, 20, 50]}
              paginationAutoPageSize={false}
              suppressPaginationPanel={false}
              paginationNumberFormatter={(params) => {
                return params.value.toLocaleString();
              }}
              localeText={{
                page: "Page",
                more: "More",
                to: "to",
                of: "of",
                next: "Next",
                last: "Last",
                first: "First",
                previous: "Previous",
                loadingOoo: "Loading...",
                noRowsToShow: "No campaigns to show"
              }}
              rowHeight={35}
              loading={false}
            />
          </Box>
        </Box>
      )}
    </Container>
  );
}

export default Campaigns;
